<script setup>
    import { getEnv } from '~/composables/helpers/getEnv';
    import { isArchivedSession, isSessionOnDemand } from '~~/shared/utilities/schedule';
    import { useScheduleControls } from '@/composables/useScheduleControls';
    const { loggedIn, user, fetch: refreshSession } = useUserSession()
    const { showSeatReservations } = useScheduleControls();
    const { $bus } = useNuxtApp();
    const userApi = useUserApi();

    const cardHover = ref(false)
    const ended = useRouteQuery('ended', false)

    const props = defineProps({
        session: {
            type: Object,
            required: true
        },
        day: {
            type: Boolean,
            default: false
        },
        showFullCard: {
            type: Boolean,
            default: false
        },
        track_index: {
            type: Number,
            default: 0
        },
        past: {
            type: Boolean,
            default: false
        },
        url: {
            type: String,
            default: 'session'
        },
        disableLink: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits(['load']);

    const isFavorite = ref(null)
    const busy = ref(false)

    const buttonStyles = {
        leadingIcon: 'h-[1.0625rem] w-[1.0625rem]',
        base: 'p-0',
    }

    const buttonStylesFilled = {
        leadingIcon: 'h-[1.0625rem] w-[1.0625rem] text-orange',
        base: 'p-0',
    }

    const hasPassed = computed(() => {
        if (!props.session.end_date_and_time) return false;
        const endTime = new Date(props.session.end_date_and_time);
        const currentTime = new Date();
        return ended.value ? true : currentTime > endTime;
    });

    // Archived (past-year) sessions don't show date/time/location — matches
    // compact-session-card / session header.
    const isArchive = computed(() => isArchivedSession(props.session));

    // Seat controls ride the same global release timestamp as every other Save
    // a Seat affordance (useScheduleControls → enableSeatReservationsAfter), so
    // this card can't hand out seats ahead of the agenda cards.
    // session_reservations is the limited-capacity truth everywhere; this card
    // used to read reservations_required, a separate D1 column.
    // !isArchive matters because the Archive block renders this same card for
    // past-year sessions — without it a 2025 session sprouts a seat icon the
    // moment the 2026 timestamp passes.
    const canSaveSeat = computed(() =>
        showSeatReservations.value
        && !isArchive.value
        && !props.session.is_external
        && props.session.session_reservations
        && !props.session.is_full
    );

    const session_link = computed(() => {
        if (props.session.is_external) {
            return getEnv('braindateUrl') + `?ext-id=${user.value.braindateHash}`;
        }
        const id = (props.session.unique_id || props.session.id || '').toLowerCase();
        return `/sessions/${id}`;
    });

    const is_favorited = computed(() => {
        // Check if user.favorites exists and contains the session unique_id
        const userHasFavorited = user.value?.favorites?.includes(props.session.unique_id);
        return isFavorite.value === null ? userHasFavorited : isFavorite.value;
    });

    const is_reserved = computed(() => {
        // sessionRegistration holds Bizzabo session ids, so it is compared
        // against the reconciled bizzabo_session_id — unique_id is a
        // Sessionboard friendly_id and never appears in that list.
        const bizzaboId = props.session.bizzabo_session_id;
        return !!bizzaboId && !!user.value?.sessionRegistration?.includes(bizzaboId);
    });

    const handleFavoriteClick = async () => {
        if (!loggedIn.value) {
            await navigateTo({ path: '/login', query: { action: 'favorite' } })
            return
        }
        try {
            busy.value = true
            isFavorite.value = true
            await userApi.favorite(props.session.unique_id);
            await refreshSession();
            $bus.emit('favorited', props.session);
        } catch (error) {
            window.alert('There was an error adding this session to your favorites. Please try again later.')
            isFavorite.value = null
        } finally {
            busy.value = false
        }
    };

    const handleUnfavoriteClick = async () => {
        if (!loggedIn.value) {
            await navigateTo({ path: '/login' })
            return
        }
        try {
            busy.value = true
            isFavorite.value = false
            await userApi.unfavorite(props.session.unique_id);
            await refreshSession();
        } catch (error) {
            window.alert('There was an error adding this session to your favorites. Please try again later.')
            isFavorite.value = null
        } finally {
            busy.value = false
        }
    };

    const handleReservationClick = async () => {
        if (!loggedIn.value) {
            await navigateTo({ path: '/login', query: { action: 'reservations' } });
            return;
        }
        await navigateTo({ path: `/reservations/${props.session.id}` });
        return;
    };

    const handleUnreservationClick = async () => {
        if (!loggedIn.value) {
            await navigateTo({ path: '/login', query: { action: 'reservations' } });
            return;
        }
        await navigateTo({ path: `/leave/${props.session.id}` });
        return;
    };

    const handleTitleClick = async (event) => {
        if (props.disableLink) {
            event.preventDefault()
            emit('load', props.session)
        }
    };

</script>
<template>
    <div class="session-cal-card flex flex-col bg-white rounded-[var(--radius)] w-[calc(100%-2rem)] mx-auto py-3 px-[0.9375rem] overflow-clip relative transition-all duration-200 ease-in-out" :class="{ 'past': past || hasPassed, 'full': showFullCard, 'is_full': session.is_full, 'alt': track_index % 2 !== 0 }">
        <div class="info">
            <UIcon v-if="isSessionOnDemand(session)" name="local-on-demand" class="on-demand absolute top-[1.0625rem] right-[0.9375rem] w-[0.78625rem] h-2 text-teal-light" />
            <p class="day time font-medium uppercase mb-0" v-if="!day && !isArchive">{{ session.display_date[0] }}&nbsp; <span class='bull opacity-50'>&bull;</span>&nbsp; {{ session.display_date[1] }}</p>
            <p class="time text-[0.8125rem] font-normal leading-[1.125rem] mb-[0.3125rem] tracking-[0] text-teal-gray" v-if="!isArchive && session.display_date?.length > 2" v-html="session.display_date[2]"></p>
            <MessLink :href="session_link" :label="session.title" @mouseover="cardHover = true" @mouseleave="cardHover = false" @click="handleTitleClick" external>
                <h4 class="text-[0.9375rem] font-medium leading-5 text-balance">{{ session.title }}</h4>
            </MessLink>
            <p class="powered-by flex flex-col gap-1 text-teal-light text-xs font-normal leading-[1.125rem] mt-[0.1rem] tracking-[0]" v-if="session.sponsors">
                <span v-for="sponsor in session.sponsors" class="sponsor">Powered by {{ sponsor.name }}</span>
            </p>
        </div>
        <div class="actions flex mt-auto gap-[1.125rem] pt-[1.125rem]" v-if="session.unique_id">
            <ClientOnly>
                <!-- favorites retired for 2026 -->
                <UButton v-if="false && !session.is_external && is_favorited" variant="transparent" icon="local-heart-filled" :ui="buttonStyles" aria-label="Unfavorite this session" @click.prevent="handleUnfavoriteClick" class='text-orange'></UButton>
                <UButton v-else-if="false && !session.is_external" variant="transparent" icon="local-heart-empty" :ui="buttonStyles" aria-label="Favorite this session" @click.prevent="handleFavoriteClick"></UButton>
                <UButton v-if="canSaveSeat && !is_reserved" variant="transparent" :ui="buttonStyles" icon="local-seat-empty" aria-label="Reserve a seat to this session" @click.prevent="handleReservationClick"></UButton>
                <UButton v-if="canSaveSeat && is_reserved" variant="transparent" :ui="buttonStylesFilled" icon="local-seat-filled" aria-label="Remove this seat" @click.prevent="handleUnreservationClick"></UButton>
            </ClientOnly>
        </div>
    </div>
</template>

<style>
    .session-cal-card.is_full {
        background-color: var(--cream);
        border: 1px solid var(--teal-gray);
    }

    .session-cal-card.is_full.alt {
        background-color: var(--cream-alt);
    }

    .session-cal-card.past {
        background-color: var(--past);
        border: none;
    }

    .session-cal-card.past .time {
        color: var(--teal);
    }

    .session-cal-card.past button span.iconify {
        color: var(--teal);
    }

    .session-cal-card.past button span.iconify:hover {
        color: var(--orange-dark);
    }

    @media (hover: hover) {
        .session-cal-card:hover {
            box-shadow: inset 0 0 0 1px var(--cream-light);
        }
    }

    .session-cal-card .day.time {
        margin-bottom: 0rem;
    }

    @media (hover: hover) {
        .session-cal-card h4:hover {
            color: var(--orange-dark);
        }
    }

    .session-cal-card.session-short h4 {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .session-cal-card .full {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        color: var(--teal-light);
        font-size: 0.75rem;
        font-weight: 400;
        line-height: 1.125rem;
        margin-top: 0.1rem;
        letter-spacing: 0;
    }
</style>
